import { useEffect, useRef, useState, cloneElement } from 'react';
import {
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Divider,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const OPEN_EVENT = 'seenc-dropdown:open';

export default function SeencDropdown({
  options = [],
  onSelect,
  children,
  popperProps,
  paperProps,
  menuListProps,
  openDelay = 120,
  closeDelay = 220,
  zIndex = undefined,
  autoFocusRoot = false,
}) {
  const [open, setOpen] = useState(false);
  const [rootAnchor, setRootAnchor] = useState(null);
  const triggerRef = useRef(null);
  const idRef = useRef(`seenc_${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    const onOpen = (e) => {
      if (e?.detail?.id !== idRef.current) setOpen(false);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { id: idRef.current } }));
    const isInside = (node) => {
      if (!node) return false;
      if (triggerRef.current && triggerRef.current.contains(node)) return true;
      if (node.closest?.('[data-seenc-menu="true"]')) return true;
      return false;
    };
    const closeIfOutside = (e) => {
      const t = e?.target || null;
      if (!isInside(t)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', closeIfOutside, true);
    window.addEventListener('wheel', closeIfOutside, { passive: true, capture: true });
    window.addEventListener('scroll', closeIfOutside, true);
    window.addEventListener('resize', () => setOpen(false));
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', closeIfOutside, true);
      window.removeEventListener('wheel', closeIfOutside, true);
      window.removeEventListener('scroll', closeIfOutside, true);
      window.removeEventListener('resize', () => setOpen(false));
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const handleToggle = (event) => {
    setRootAnchor(event.currentTarget);
    setOpen((prev) => !prev);
  };

  const handleClose = (event) => {
    if (triggerRef.current && triggerRef.current.contains?.(event?.target)) return;
    setOpen(false);
  };

  if (!children) return null;

  const triggerElement = cloneElement(children, {
    ref: (node) => {
      triggerRef.current = node;
      if (typeof children.ref === 'function') children.ref(node);
    },
    'data-seenc-dropdown-trigger': 'true',
    onClick: (event) => {
      children.props.onClick?.(event);
      handleToggle(event);
    },
    'aria-controls': open ? 'seenc-menu-root' : undefined,
    'aria-expanded': open ? 'true' : undefined,
    'aria-haspopup': 'true',
  });

  return (
    <>
      {triggerElement}
      <Popper
        open={open}
        anchorEl={rootAnchor}
        placement='bottom-start'
        transition
        disablePortal={false}
        modifiers={[
          { name: 'offset', options: { offset: [0, 4] } },
          { name: 'flip', options: { fallbackPlacements: ['bottom-end', 'top-start'] } },
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8, tether: true } },
        ]}
        sx={{ zIndex: (theme) => zIndex ?? theme.zIndex.modal + 10 }}
        {...popperProps}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom' }}>
            <Paper
              elevation={8}
              data-seenc-menu='true'
              {...paperProps}>
              <ClickAwayListener onClickAway={handleClose}>
                <RecursiveMenuList
                  id='seenc-menu-root'
                  options={options}
                  onClose={handleClose}
                  onLeafClick={(opt) => {
                    opt.onClick?.();
                    onSelect?.(opt);
                    setOpen(false);
                  }}
                  depth={0}
                  menuListProps={menuListProps}
                  openDelay={openDelay}
                  closeDelay={closeDelay}
                  zIndex={zIndex}
                  autoFocusRoot={autoFocusRoot}
                />
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

function RecursiveMenuList({
  id,
  options,
  onClose,
  onLeafClick,
  depth,
  menuListProps,
  openDelay,
  closeDelay,
  zIndex,
  autoFocusRoot,
}) {
  const [activeIdx, setActiveIdx] = useState(null);
  const [anchors, setAnchors] = useState({});
  const timersRef = useRef({});

  const clearTimers = (idx) => {
    const t = timersRef.current[idx];
    if (!t) return;
    if (t.open) clearTimeout(t.open);
    if (t.close) clearTimeout(t.close);
    timersRef.current[idx] = {};
  };

  const scheduleOpen = (idx, anchorEl) => {
    clearTimers(idx);
    timersRef.current[idx] = {
      ...timersRef.current[idx],
      open: setTimeout(() => {
        setAnchors((p) => ({ ...p, [idx]: anchorEl }));
        setActiveIdx(idx);
      }, openDelay),
    };
  };

  const scheduleClose = (idx) => {
    clearTimers(idx);
    timersRef.current[idx] = {
      ...timersRef.current[idx],
      close: setTimeout(() => {
        setActiveIdx((cur) => (cur === idx ? null : cur));
      }, closeDelay),
    };
  };

  const handleParentEnter = (idx) => (e) => scheduleOpen(idx, e.currentTarget);
  const handleParentLeave = (idx) => () => scheduleClose(idx);

  const handleParentClick = (idx) => (e) => {
    if (activeIdx === idx) {
      setActiveIdx(null);
      return;
    }
    clearTimers(idx);
    setAnchors((p) => ({ ...p, [idx]: e.currentTarget }));
    setActiveIdx(idx);
  };

  const handleSubEnter = (idx) => () => {
    const t = timersRef.current[idx];
    if (t?.close) clearTimeout(t.close);
  };

  const handleSubLeave = (idx) => () => scheduleClose(idx);

  const handleKeyDown = (event) => {
    if (event.key === 'Tab' || event.key === 'Escape') {
      event.preventDefault?.();
      onClose?.(event);
    }
  };

  return (
    <MenuList
      id={id}
      autoFocusItem={depth === 0 && !!autoFocusRoot}
      onKeyDown={handleKeyDown}
      dense
      {...menuListProps}>
      {options?.length > 0 ?
        options.map((option, idx) => {
          if (option?.isDivider) {
            return (
              <Divider
                key={`div-${depth}-${idx}`}
                sx={{ my: 0.5 }}
              />
            );
          }

          const childrenOptions = option.options || option.children;
          const hasChildren = Array.isArray(childrenOptions) && childrenOptions.length > 0;
          const key = option.key ?? option.label ?? `${depth}-${idx}`;

          if (hasChildren) {
            const open = activeIdx === idx;
            const anchorEl = anchors[idx] || null;

            return (
              <MenuItem
                key={key}
                disabled={option.disabled}
                onPointerEnter={handleParentEnter(idx)}
                onPointerLeave={handleParentLeave(idx)}
                onClick={handleParentClick(idx)}
                onMouseDown={(e) => e.preventDefault()}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}>
                <ListItemText>{option.label}</ListItemText>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ChevronRightIcon fontSize='small' />
                </ListItemIcon>

                <Popper
                  open={open}
                  anchorEl={anchorEl}
                  placement='right-start'
                  transition
                  disablePortal={false}
                  modifiers={[
                    { name: 'offset', options: { offset: [0, 0] } },
                    { name: 'flip', options: { fallbackPlacements: ['left-start'] } },
                    { name: 'preventOverflow', options: { padding: 8, boundary: 'viewport' } },
                  ]}
                  sx={{ zIndex: (theme) => zIndex ?? theme.zIndex.modal + 10 }}>
                  {({ TransitionProps }) => (
                    <Grow
                      {...TransitionProps}
                      style={{ transformOrigin: 'left top' }}>
                      <Paper
                        data-seenc-menu='true'
                        onPointerEnter={handleSubEnter(idx)}
                        onPointerLeave={handleSubLeave(idx)}>
                        <RecursiveMenuList
                          id={`seenc-menu-${depth + 1}-${idx}`}
                          options={childrenOptions}
                          onClose={() => setActiveIdx(null)}
                          onLeafClick={(opt) => {
                            opt.onClick?.(opt);
                            onLeafClick?.(opt);
                          }}
                          depth={depth + 1}
                          menuListProps={menuListProps}
                          openDelay={openDelay}
                          closeDelay={closeDelay}
                          zIndex={zIndex}
                          autoFocusRoot={autoFocusRoot}
                        />
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </MenuItem>
            );
          }

          return (
            <MenuItem
              key={key}
              disabled={option.disabled}
              onClick={() => onLeafClick?.(option)}>
              {option.label}
            </MenuItem>
          );
        })
      : <MenuItem disabled>Sem ações</MenuItem>}
    </MenuList>
  );
}
